import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, TextField,
  Chip, useTheme, useMediaQuery,
  IconButton, Alert, Button, CircularProgress, LinearProgress,
  Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import {
  DashboardOutlined, Refresh, Menu as MenuIcon,
  AccessTime, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Share,
  BookmarksOutlined, ArrowBack,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import apiClient from 'utils/ApiClient';
import { useModulePermissions } from 'hooks/useModulePermissions';
import { ReportPanel } from './ReportPanel';
import { ReportPanelResizeWrapper } from './ReportPanelResizeWrapper';
import { CounterTile } from './CounterTile';
import { RealtimePanel } from './RealtimePanel';
import {
  ReportDefinition, ReportSuiteDetail, TimeRangeDef, FilterDef, sortReportsByLayout,
} from '../Settings/reportDsl';
import { useReportsTimeRange } from './hooks/useReportsTimeRange';
import { TimePickerPopover } from '../Logs/components/TimePickerPopover';
import { LOGO_BASE, MODULE_LOGO_MAP } from '../Logs/types';
import { REPORTS_QUICK_RANGES, REPORTS_CUSTOM_TIME_MAX_MS } from './reportsTimeRange';
import {
  reportsToolbarSurfaceSx,
  reportsTimeRangeIconButtonSx,
  reportsTimeRangePickerButtonSx,
  reportsToolbarIconButtonSx,
  reportsToolbarLoadingLinearProgressSx,
  reportsToolbarAccentColor,
} from './reportsToolbarSurface';
import {
  fetchDefaultReportsClickHouseConfigId,
  hasReportsLocalSelectionHistory,
  REPORTS_STORAGE_KEY_SERVER,
  REPORTS_STORAGE_KEY_SUITE,
  resolveReportsSuiteId,
} from '../reportsDefaultServer';
import { ShareReportsDialog } from '../DirectReports/ShareReportsDialog';
import { resolveLinkedL2Reports, resolveLinkedPanelReports } from './resolveLinkedL2Reports';

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  tenantId?: string;
  siteId?: string;
  defaultReportSuiteId?: string;
  defaultTimeRange?: string;
  reportSuites?: Array<{ _id: string; name: string; description?: string }>;
}

interface SuiteOption {
  configId: string;
  configName: string;
  tenantId: string;
  siteId: string;
  suiteId: string;
  suiteName: string;
  description?: string;
}

interface ReportSuitePick {
  _id: string;
  name: string;
  description?: string;
}

const filterReportSuiteOptions = (
  options: ReportSuitePick[],
  state: { inputValue: string },
): ReportSuitePick[] => {
  const q = state.inputValue.trim().toLowerCase();
  if (!q) return options;
  return options.filter((s) => {
    const name = s.name.toLowerCase();
    const desc = (s.description || '').toLowerCase();
    return name.includes(q) || desc.includes(q);
  });
};

const ReportSuiteFilter: React.FC<{
  suites: ReportSuitePick[];
  valueId: string;
  onChange: (suiteId: string) => void;
  disabled: boolean;
  fullWidth?: boolean;
  hiddenOnMobile?: boolean;
}> = ({ suites, valueId, onChange, disabled, fullWidth, hiddenOnMobile }) => {
  const selected = suites.find((s) => s._id === valueId) ?? null;
  return (
    <Autocomplete<ReportSuitePick, false, boolean>
      size="small"
      fullWidth={fullWidth}
      options={suites}
      value={selected}
      onChange={(_e, suite) => {
        if (suite) onChange(suite._id);
      }}
      getOptionLabel={(s) => s.name}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      filterOptions={filterReportSuiteOptions}
      disabled={disabled}
      autoHighlight
      disableClearable={Boolean(selected)}
      ListboxProps={{ sx: { maxHeight: 360 } }}
      noOptionsText={
        suites.length === 0
          ? (disabled ? 'Select a server first' : 'No report suites configured')
          : 'No matching report suites'
      }
      sx={{
        minWidth: hiddenOnMobile ? 240 : undefined,
        flex: hiddenOnMobile ? 1 : undefined,
        display: hiddenOnMobile ? { xs: 'none', md: 'inline-flex' } : undefined,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Report Suite"
          placeholder="Type to filter…"
        />
      )}
    />
  );
};

const validateSuiteKey = (
  key: string,
  clickhouseConfigs: ModuleConfig[],
  defaultConfigId: string | null,
): string => {
  if (clickhouseConfigs.length === 0) return '';

  let configId = '';
  let suiteId = '';
  if (key?.includes(':')) {
    [configId, suiteId] = key.split(':');
  }

  if (!configId) {
    const savedServer = localStorage.getItem(REPORTS_STORAGE_KEY_SERVER);
    if (savedServer && clickhouseConfigs.some((c) => c._id === savedServer)) {
      configId = savedServer;
    } else if (
      !hasReportsLocalSelectionHistory()
      && defaultConfigId
      && clickhouseConfigs.some((c) => c._id === defaultConfigId)
    ) {
      configId = defaultConfigId;
    } else {
      configId = clickhouseConfigs[0]._id;
    }
  }

  const cfg = clickhouseConfigs.find((c) => c._id === configId);
  if (!cfg) {
    const c0 = clickhouseConfigs[0];
    const s0 = c0.reportSuites?.[0];
    return s0 ? `${c0._id}:${s0._id}` : '';
  }

  const suites = cfg.reportSuites ?? [];
  if (suites.length === 0) return '';

  if (suiteId && suites.some((s) => s._id === suiteId)) {
    return `${configId}:${suiteId}`;
  }

  const resolvedSuiteId = resolveReportsSuiteId(cfg, { savedSuiteId: suiteId || null });
  return resolvedSuiteId ? `${configId}:${resolvedSuiteId}` : '';
};

const msToChDateTime = (ms: number): string =>
  new Date(ms).toISOString().replace('T', ' ').replace('Z', '').slice(0, 23);

export const Reports: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  // Optional route params: /…/reports/:suiteId/drilldown/:reportId opens a
  // panel drill-down (level-1) directly from the URL so it is deep-linkable.
  const { suiteId: routeSuiteId, reportId: routeReportId } = useParams<{
    suiteId?: string;
    reportId?: string;
  }>();

  // Mobile: server / suite / Logs-style time range live in the dialog opened
  // from the burger icon (same pattern as LogToolbar compaction).
  const [mobileSuiteDialogOpen, setMobileSuiteDialogOpen] = useState(false);

  // "Share" dialog: build an SSO deep-link to the standalone Direct Reports page.
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const [configs, setConfigs] = useState<ModuleConfig[]>([]);
  const [configsLoading, setConfigsLoading] = useState(true);
  const [defaultReportsClickHouseConfigId, setDefaultReportsClickHouseConfigId] = useState<string | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<string>(
    () => localStorage.getItem(REPORTS_STORAGE_KEY_SERVER) || '',
  );
  const [activeSuiteKey, setActiveSuiteKey] = useState<string>(() => localStorage.getItem(REPORTS_STORAGE_KEY_SUITE) || '');
  /** False until configs load and the suite key is reconciled with localStorage/defaults. */
  const [suiteKeyResolved, setSuiteKeyResolved] = useState(false);
  const [suiteDetail, setSuiteDetail] = useState<ReportSuiteDetail | null>(null);
  const [loadingSuite, setLoadingSuite] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  /**
   * Mirror the Settings menu gating in src/components/Sidebar/Sidebar.tsx:
   * the resize handle is offered only to users who can manage Infralytiqs
   * (admin role on `infralytiqs.com`) and to super-admins. The PATCH endpoint
   * is also guarded by `canManageInfralytiqs` server-side, so anything that
   * bypasses this check would still be rejected.
   */
  const { hasInfralytiqsAccess, isUserSuperAdmin } = useModulePermissions();
  const canResizeReportPanels = hasInfralytiqsAccess('admin') || isUserSuperAdmin;
  const canReorderReportPanels = canResizeReportPanels;
  // The per-panel Edit button (deep-links to the suite config editor) is only
  // for infralytiqs.com admins (and super-admins), matching settings access.
  const canEditReportPanels = hasInfralytiqsAccess('admin') || isUserSuperAdmin;

  /** ID of the panel currently being dragged for reorder, if any. */
  const [reorderDragId, setReorderDragId] = useState<string | null>(null);

  /**
   * Modal drill-down "stack" opened from table row clicks whose
   * `onRowClick.passField` is something other than `user_id`. Each frame is a
   * report rendered in the modal, narrowed by the accumulated runtime filters
   * of every level above it. This supports multi-level drill paths such as
   * Search Terms (aggregated) → per-term event list → single-event detail card,
   * while leaving the existing user_id deep-link navigation untouched.
   */
  const [drillStack, setDrillStack] = useState<
    Array<{ report: ReportDefinition; runtimeFilters: FilterDef[]; title: string }>
  >([]);

  // `/analytics/*` (Campaign King) and `/infralytiqs/*` (Instant Update) both
  // render this component — keep drill-down routes on the same top-level prefix
  // so breadcrumbs stay consistent with the menu the user came from.
  const routePrefix = location.pathname.startsWith('/analytics')
    ? '/analytics/reports'
    : '/infralytiqs/reports';

  const fetchConfigs = useCallback(async () => {
    setConfigsLoading(true);
    try {
      const [res, defaultConfigId]: [any, string | null] = await Promise.all([
        apiClient.get('/il/settings/module-configs'),
        fetchDefaultReportsClickHouseConfigId(),
      ]);
      const list: ModuleConfig[] = Array.isArray(res?.data) ? res.data : (res?.data?.moduleConfigurations ?? []);
      const onlyClickhouse = list.filter((c) => c.moduleType === 'ClickHouse');
      setConfigs(onlyClickhouse);
      setDefaultReportsClickHouseConfigId(defaultConfigId);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to load configurations');
    } finally {
      setConfigsLoading(false);
    }
  }, []);

  useEffect(() => { void fetchConfigs(); }, [fetchConfigs]);

  useEffect(() => {
    if (configs.length === 0) return;
    const next = validateSuiteKey(activeSuiteKey, configs, defaultReportsClickHouseConfigId);
    const nextConfigId = next.includes(':') ? next.split(':')[0] : (
      selectedConfigId && configs.some((c) => c._id === selectedConfigId)
        ? selectedConfigId
        : (
          !hasReportsLocalSelectionHistory()
          && defaultReportsClickHouseConfigId
          && configs.some((c) => c._id === defaultReportsClickHouseConfigId)
            ? defaultReportsClickHouseConfigId
            : configs[0]._id
        )
    );
    if (nextConfigId !== selectedConfigId) {
      setSelectedConfigId(nextConfigId);
      localStorage.setItem(REPORTS_STORAGE_KEY_SERVER, nextConfigId);
    }
    if (next !== activeSuiteKey) {
      setActiveSuiteKey(next);
      if (next) {
        localStorage.setItem(REPORTS_STORAGE_KEY_SUITE, next);
      } else {
        localStorage.removeItem(REPORTS_STORAGE_KEY_SUITE);
      }
    }
    setSuiteKeyResolved(true);
  }, [configs, activeSuiteKey, defaultReportsClickHouseConfigId]);

  const activeConfigId = selectedConfigId || (activeSuiteKey.includes(':') ? activeSuiteKey.split(':')[0] : '');
  const activeSuiteIdOnly = activeSuiteKey.includes(':') ? activeSuiteKey.split(':')[1] : '';

  const suitesForConfig = useMemo(
    () => (configs.find((c) => c._id === activeConfigId)?.reportSuites ?? []),
    [configs, activeConfigId],
  );

  // Time-range selection is remembered per server + suite. When a server/suite
  // has no stored history, the server's configured default time range applies.
  const reportsTimeRangeScopeKey = activeConfigId && activeSuiteIdOnly
    ? `${activeConfigId}::${activeSuiteIdOnly}`
    : '';
  const reportsDefaultTimeRangeLabel = useMemo(
    () => configs.find((c) => c._id === activeConfigId)?.defaultTimeRange || '',
    [configs, activeConfigId],
  );
  const timeRange = useReportsTimeRange({
    scopeKey: reportsTimeRangeScopeKey,
    defaultTimeRangeLabel: reportsDefaultTimeRangeLabel,
  });
  const pickerDates = timeRange.getPickerInitialDates();

  const activeSuiteOpt = useMemo((): SuiteOption | null => {
    if (!activeConfigId || !activeSuiteIdOnly) return null;
    const cfg = configs.find((c) => c._id === activeConfigId);
    if (!cfg) return null;
    const suite = (cfg.reportSuites || []).find((s) => s._id === activeSuiteIdOnly);
    if (!suite) return null;
    return {
      configId: cfg._id,
      configName: cfg.name,
      tenantId: cfg.tenantId || '',
      siteId: cfg.siteId || '',
      suiteId: suite._id,
      suiteName: suite.name,
      description: suite.description,
    };
  }, [configs, activeConfigId, activeSuiteIdOnly]);

  const persistAndSetSuiteKey = (key: string) => {
    setActiveSuiteKey(key);
    if (key) localStorage.setItem(REPORTS_STORAGE_KEY_SUITE, key);
  };

  const handleConfigChange = (configId: string) => {
    // Leave any drill-down URL when switching server so the path stays in sync.
    if (routeSuiteId || routeReportId) navigate(routePrefix);
    setSelectedConfigId(configId);
    localStorage.setItem(REPORTS_STORAGE_KEY_SERVER, configId);
    const cfg = configs.find((c) => c._id === configId);
    const resolvedSuiteId = cfg ? resolveReportsSuiteId(cfg) : null;
    if (resolvedSuiteId) {
      persistAndSetSuiteKey(`${configId}:${resolvedSuiteId}`);
    } else {
      setActiveSuiteKey('');
      setSuiteDetail(null);
      localStorage.removeItem(REPORTS_STORAGE_KEY_SUITE);
    }
  };

  const handleSuiteChange = (suiteId: string) => {
    if (!activeConfigId) return;
    if (routeSuiteId || routeReportId) navigate(routePrefix);
    persistAndSetSuiteKey(`${activeConfigId}:${suiteId}`);
  };

  const timeRangeOverride = useMemo((): TimeRangeDef => (
    timeRange.activePreset
      ? { preset: timeRange.activePreset }
      : (() => {
        const { start, end } = timeRange.getTimeRangeMs();
        return { start: msToChDateTime(start), end: msToChDateTime(end) };
      })()
  ), [timeRange.activePreset, timeRange.getTimeRangeMs]);

  const selectedConfig = useMemo(
    () => configs.find((c) => c._id === activeConfigId) ?? null,
    [configs, activeConfigId],
  );

  const serverHasNoSuites = Boolean(activeConfigId && suitesForConfig.length === 0);

  const fetchSuiteDetail = useCallback(async () => {
    if (!suiteKeyResolved) return;
    if (!activeSuiteOpt) {
      setSuiteDetail(null);
      return;
    }
    setLoadingSuite(true);
    setErrorMsg(null);
    try {
      const res: any = await apiClient.get(
        `/il/settings/module-config/${activeSuiteOpt.configId}/report-suites/${activeSuiteOpt.suiteId}`,
      );
      setSuiteDetail(res?.data ?? null);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to load suite');
    } finally {
      setLoadingSuite(false);
    }
  }, [activeSuiteOpt, suiteKeyResolved]);

  useEffect(() => { void fetchSuiteDetail(); }, [fetchSuiteDetail]);

  /**
   * Persist a panel resize. We optimistically patch local `suiteDetail` so the
   * grid re-flows immediately, then PATCH the report on the server. On failure
   * we refetch the suite to drop the optimistic value and surface the error.
   */
  const handleReportLayoutChange = useCallback(
    async (
      report: ReportDefinition,
      layout: { x: number; y: number; w: number; h: number },
    ) => {
      if (!activeSuiteOpt || !report._id) return;

      setSuiteDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          reports: prev.reports.map((r) => (r._id === report._id ? { ...r, layout } : r)),
        };
      });

      try {
        await apiClient.patch(
          `/il/settings/module-config/${activeSuiteOpt.configId}/report-suites/${activeSuiteOpt.suiteId}/reports/${report._id}`,
          { layout },
        );
      } catch (err: any) {
        setErrorMsg(err?.message || 'Failed to save panel size');
        void fetchSuiteDetail();
      }
    },
    [activeSuiteOpt, fetchSuiteDetail],
  );

  /**
   * Persist a drag-to-reorder. We rebuild the dashboard panels in their new
   * order (moving `sourceId` to before/after `targetId`), then re-number
   * `layout.y` sequentially across the dashboard panels (0, 1, 2, …). Only
   * reports whose y actually changed are PATCHed. Non-dashboard reports
   * (toolbar / realtime / drilldown) live in their own visual slots and are
   * not re-numbered here.
   */
  const handleReportReorder = useCallback(
    async (sourceId: string, targetId: string, position: 'before' | 'after') => {
      if (!activeSuiteOpt || !suiteDetail || sourceId === targetId) return;

      // Recompute the dashboard ordering inline (we can't reference the
      // memoized `dashboardReports` here — it's declared further down in the
      // render and would cause a TDZ error in this useCallback's deps).
      const isToolbarRpt = (r: ReportDefinition) => r.mode === 'toolbar';
      const isRealtimeRpt = (r: ReportDefinition) => {
        const t = r.visualizations?.[0]?.type;
        return t === 'realtime' || t === 'realtime_bar';
      };
      const currentOrder = sortReportsByLayout(
        suiteDetail.reports.filter(
          (r) => r.mode !== 'drilldown' && !isToolbarRpt(r) && !isRealtimeRpt(r),
        ),
      );
      const fromIdx = currentOrder.findIndex((r) => r._id === sourceId);
      if (fromIdx < 0) return;

      const [moved] = currentOrder.splice(fromIdx, 1);
      let insertIdx = currentOrder.findIndex((r) => r._id === targetId);
      if (insertIdx < 0) return;
      if (position === 'after') insertIdx += 1;
      currentOrder.splice(insertIdx, 0, moved);

      const updates: Array<{
        id: string;
        layout: { x: number; y: number; w: number; h: number };
      }> = [];
      currentOrder.forEach((r, i) => {
        if (!r._id) return;
        const base = r.layout || { x: 0, y: 0, w: 12, h: 4 };
        if (base.y !== i) {
          updates.push({ id: r._id, layout: { ...base, y: i } });
        }
      });
      if (updates.length === 0) return;

      const updateMap = new Map(updates.map((u) => [u.id, u.layout]));
      setSuiteDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          reports: prev.reports.map((r) => {
            const nextLayout = r._id ? updateMap.get(r._id) : undefined;
            return nextLayout ? { ...r, layout: nextLayout } : r;
          }),
        };
      });

      try {
        await Promise.all(
          updates.map((u) => apiClient.patch(
            `/il/settings/module-config/${activeSuiteOpt.configId}/report-suites/${activeSuiteOpt.suiteId}/reports/${u.id}`,
            { layout: u.layout },
          )),
        );
      } catch (err: any) {
        setErrorMsg(err?.message || 'Failed to save panel order');
        void fetchSuiteDetail();
      }
    },
    [activeSuiteOpt, suiteDetail, fetchSuiteDetail],
  );

  /**
   * Deep-link support: when the URL carries `/:suiteId/drilldown/:reportId`,
   * make that suite the active one (resolving its owning ClickHouse config)
   * so the panel drill-down for `:reportId` renders even on a fresh load /
   * shared link.
   */
  useEffect(() => {
    if (!routeSuiteId || configs.length === 0) return;
    if (activeSuiteIdOnly === routeSuiteId) return;
    const owning = configs.find((c) => (c.reportSuites || []).some((s) => s._id === routeSuiteId));
    if (!owning) return;
    const key = `${owning._id}:${routeSuiteId}`;
    setSelectedConfigId(owning._id);
    localStorage.setItem(REPORTS_STORAGE_KEY_SERVER, owning._id);
    setActiveSuiteKey(key);
    localStorage.setItem(REPORTS_STORAGE_KEY_SUITE, key);
  }, [routeSuiteId, configs, activeSuiteIdOnly]);

  // Split reports by mode / visualization type so each one renders in the
  // right slot of the page. `mode` is the controlling signal:
  //
  //   - mode='toolbar'                  → compact tiles inline in the toolbar
  //                                       (only when the user explicitly opts
  //                                       in via the Report Suite editor)
  //   - vis='realtime' (mode≠drilldown) → full-width "live" line graphs
  //                                       pinned above the dashboard grid
  //   - mode='drilldown'                → hidden until navigated to via a
  //                                       row click on a parent table
  //   - everything else                 → standard dashboard grid panels
  //                                       (this includes vis='counter', which
  //                                       renders as a hero KPI panel)
  //
  // Sort by `layout.y` so the order set in the Report Suite editor matches
  // exactly what users see on the dashboard. Reports without an explicit y
  // fall back to their original position via the stable tiebreaker.
  const allReports = suiteDetail?.reports ?? [];
  const drilldownReports = allReports.filter((r) => r.mode === 'drilldown');

  const isToolbar = (r: ReportDefinition) => r.mode === 'toolbar';
  // Both `realtime` (line) and `realtime_bar` (bar) are rendered by the
  // same RealtimePanel component — the visualization type just controls
  // the chart shape. Treat them as the same "realtime" bucket here.
  const isRealtime = (r: ReportDefinition) => {
    const t = r.visualizations?.[0]?.type;
    return t === 'realtime' || t === 'realtime_bar';
  };

  const toolbarReports = sortReportsByLayout(
    allReports.filter((r) => isToolbar(r) && !isRealtime(r)),
  );

  const realtimeReports = sortReportsByLayout(
    allReports.filter((r) => r.mode !== 'drilldown' && !isToolbar(r) && isRealtime(r)),
  );

  const dashboardReports = sortReportsByLayout(
    allReports.filter((r) => r.mode !== 'drilldown' && !isToolbar(r) && !isRealtime(r)),
  );

  /**
   * The panel drill-down (level-1) is driven entirely by the URL: when
   * `/:suiteId/drilldown/:reportId` matches a drill-down report in the active
   * suite, we render it below the toolbar. This keeps it deep-linkable and lets
   * the breadcrumb / browser back button work naturally.
   */
  const activeDrilldownReport = useMemo(
    () => (routeReportId ? drilldownReports.find((r) => r._id === routeReportId) : undefined),
    [routeReportId, drilldownReports],
  );

  // The dashboard panel whose click opened this drill-down (for the "From:" caption).
  const drilldownSourceReport = useMemo(() => {
    if (!activeDrilldownReport) return undefined;
    return allReports.find(
      (r) =>
        r.interactions?.onPanelClick?.targetReportId === activeDrilldownReport._id ||
        r.interactions?.onPanelClick?.targetReportName === activeDrilldownReport.name,
    );
  }, [activeDrilldownReport, allReports]);

  const panelDrilldown = activeDrilldownReport
    ? { targetReport: activeDrilldownReport, sourceTitle: drilldownSourceReport?.name || '' }
    : null;

  const drilldownViewTitle = panelDrilldown
    ? (panelDrilldown.targetReport.visualizations?.[0]?.title || panelDrilldown.targetReport.name)
    : null;

  /**
   * A level-1 drill-down page can be narrowed to a single parent value (e.g.
   * the Search Terms panel passes the clicked `search_term`). That value rides
   * in the URL query string (`?f=<field>&v=<value>`) so the page stays fully
   * deep-linkable, then is replayed here as a runtime filter on the report.
   */
  const drilldownRuntimeFilters = useMemo<FilterDef[]>(() => {
    if (!activeDrilldownReport) return [];
    const params = new URLSearchParams(location.search);
    const field = params.get('f');
    const value = params.get('v');
    if (field && value !== null) return [{ field, op: 'eq', value }];
    return [];
  }, [activeDrilldownReport, location.search]);

  /**
   * A short "<label> = <value>" badge shown in the drill-down panel's title bar
   * (mirrors the per-user page's `user_id = …` chip). The friendly label is the
   * dimension label configured on the parent panel that drills into this report
   * via `onRowClick`, falling back to the raw field name.
   */
  const drilldownBadge = useMemo<string | undefined>(() => {
    const f = drilldownRuntimeFilters[0];
    if (!f || !activeDrilldownReport) return undefined;
    const source = allReports.find(
      (r) =>
        (r.interactions?.onRowClick?.targetReportId === activeDrilldownReport._id ||
          r.interactions?.onRowClick?.targetReportName === activeDrilldownReport.name) &&
        r.interactions?.onRowClick?.passField === f.field,
    );
    const label = source?.query.dimensions?.find((d) => d.field === f.field)?.label || f.field;
    return `${label} = ${f.value}`;
  }, [drilldownRuntimeFilters, activeDrilldownReport, allReports]);

  // A modal drill-stack only makes sense within a single page; drop it whenever
  // the URL navigates to a different suite or drill-down report.
  useEffect(() => {
    setDrillStack([]);
  }, [routeSuiteId, routeReportId]);

  const parentDrilldownId = useMemo(() => {
    if (!activeDrilldownReport) return undefined;
    return new URLSearchParams(location.search).get('from') || undefined;
  }, [activeDrilldownReport, location.search]);

  const parentDrilldownReport = useMemo(
    () => (parentDrilldownId ? allReports.find((r) => r._id === parentDrilldownId) : undefined),
    [parentDrilldownId, allReports],
  );

  /**
   * L2 can show several sibling tables (e.g. Uploaded / Excluded / Already
   * synced) filtered by the same URL `f`/`v`. The parent L1 `onRowClick.level2ReportNames`
   * list is the source of order. Without that list, or without a runtime
   * filter, we render the single report in the URL.
   */
  const linkedL2Reports = useMemo(() => {
    if (!activeDrilldownReport) return [];

    // Panel-click siblings (no row filter): e.g. Standard Users → env counters.
    if (drilldownSourceReport && drilldownRuntimeFilters.length === 0) {
      const panelLinked = resolveLinkedPanelReports(
        drilldownSourceReport, allReports, drilldownReports,
      );
      if (panelLinked.length > 0) return panelLinked;
      return [activeDrilldownReport];
    }

    const parent = parentDrilldownReport || allReports.find((r) => {
      const rowClick = r.interactions?.onRowClick;
      return rowClick?.targetReportId === activeDrilldownReport._id
        || rowClick?.targetReportName === activeDrilldownReport.name
        || (rowClick?.level2ReportNames || []).includes(activeDrilldownReport.name);
    });
    if (!parent || drilldownRuntimeFilters.length === 0) {
      return [activeDrilldownReport];
    }
    const resolved = resolveLinkedL2Reports(parent, allReports, drilldownReports);
    return resolved.length > 0 ? resolved : [activeDrilldownReport];
  }, [activeDrilldownReport, parentDrilldownReport, allReports, drilldownReports, drilldownRuntimeFilters, drilldownSourceReport]);

  const breadcrumbItems = useMemo(() => {
    const items: Array<{ itemPath: string; itemLabel: string }> = [];
    if (activeDrilldownReport && drilldownViewTitle) {
      // Report Dashboard › (optional parent L1) › current drill-down.
      // L2 uses the same hash route as L1 (`…/drilldown/:reportId?f=&v=&from=`).
      items.push({ itemPath: routePrefix, itemLabel: 'Report Dashboard' });
      if (parentDrilldownReport?._id && activeSuiteOpt) {
        const parentSource = allReports.find((r) => {
          const panel = r.interactions?.onPanelClick;
          if (!panel) return false;
          return panel.targetReportId === parentDrilldownReport._id
            || panel.targetReportName === parentDrilldownReport.name
            || (panel.level2ReportNames || []).includes(parentDrilldownReport.name);
        });
        items.push({
          itemPath: `${routePrefix}/${activeSuiteOpt.suiteId}/drilldown/${parentDrilldownReport._id}`,
          itemLabel: parentSource?.name
            || parentDrilldownReport.visualizations?.[0]?.title
            || parentDrilldownReport.name,
        });
      }
      items.push({
        itemPath: '',
        itemLabel: linkedL2Reports.length > 1
          ? (drilldownSourceReport?.name || 'Run details')
          : drilldownViewTitle,
      });
    } else {
      items.push({ itemPath: '', itemLabel: 'Report Dashboard' });
    }
    return items;
  }, [
    routePrefix,
    activeDrilldownReport,
    drilldownViewTitle,
    parentDrilldownReport,
    activeSuiteOpt,
    allReports,
    linkedL2Reports.length,
    drilldownSourceReport,
  ]);

  /**
   * Clicking a row in the "Top Users" table navigates to a dedicated drill-down
   * URL so the user can deep-link / share / bookmark. The destination is only
   * available when `passField === 'user_id'` (our UserDrillDownPage is keyed by
   * userId). For any other passField we silently no-op — future work can add
   * additional per-entity landing pages.
   */
  const resolveDrilldownTarget = useCallback((
    cfg: { targetReportName?: string; targetReportId?: string } | undefined,
  ): ReportDefinition | undefined => {
    if (!cfg) return undefined;
    if (cfg.targetReportId) {
      return drilldownReports.find((r) => r._id === cfg.targetReportId);
    }
    if (cfg.targetReportName) {
      return drilldownReports.find((r) => r.name === cfg.targetReportName);
    }
    return undefined;
  }, [drilldownReports]);

  const handlePanelClick = useCallback((sourceReport: ReportDefinition) => {
    const target = resolveDrilldownTarget(sourceReport.interactions?.onPanelClick);
    if (!target?._id || !activeSuiteOpt) return;
    const params = new URLSearchParams();
    if (activeDrilldownReport?._id) {
      params.set('from', activeDrilldownReport._id);
    }
    const qs = params.toString();
    navigate(
      `${routePrefix}/${activeSuiteOpt.suiteId}/drilldown/${target._id}${qs ? `?${qs}` : ''}`,
    );
  }, [resolveDrilldownTarget, activeSuiteOpt, navigate, routePrefix, activeDrilldownReport]);

  // Deep-link to the suite settings editor with this report pre-opened for edit.
  const handleEditReport = useCallback((report: ReportDefinition) => {
    if (!activeSuiteOpt || !report._id) return;
    navigate(`/infralytiqs/settings/${activeSuiteOpt.configId}/suites/${activeSuiteOpt.suiteId}?editReport=${report._id}`);
  }, [activeSuiteOpt, navigate]);

  const handleRowClick = (row: Record<string, any>, sourceReport: ReportDefinition) => {
    const cfg = sourceReport.interactions?.onRowClick;
    if (!cfg?.passField || !activeSuiteOpt) return;
    if (drilldownReports.length === 0) return;

    const dims = sourceReport.query.dimensions || [];
    const dimIdx = dims.findIndex((d) => d.field === cfg.passField);
    const metrics = sourceReport.query.metrics || [];
    const metIdx = metrics.findIndex((m) => m.field === cfg.passField);
    const value = dimIdx >= 0
      ? row[`dim_${dimIdx}`]
      : (metIdx >= 0 ? row[`metric_${metIdx}`] : row[cfg.passField]);
    if (value === undefined || value === null || value === '') return;

    // user_id keeps its dedicated, deep-linkable per-user landing page —
    // but only from the top-level dashboard (an empty drill stack); inside a
    // modal drill we always stay in the stack.
    if (cfg.passField === 'user_id' && drillStack.length === 0) {
      // The clicked row lives in a (level-1) drill-down report; carry that
      // report id in the URL so the per-user (level-2) page can show a
      // breadcrumb back to it and so the whole path is deep-linkable.
      const encodedUser = encodeURIComponent(String(value));
      if (sourceReport._id) {
        navigate(`${routePrefix}/${activeSuiteOpt.suiteId}/drilldown/${sourceReport._id}/user/${encodedUser}`);
      } else {
        navigate(`${routePrefix}/${activeSuiteOpt.suiteId}/${encodedUser}`);
      }
      return;
    }

    // Any other passField (e.g. run_id, search_term) drills further on the same
    // hash route as level-1: `/…/reports/:suiteId/drilldown/:reportId?f=&v=`.
    // When already on a drill-down page, `from` remembers the parent report so
    // breadcrumbs can link back. Do not open a dialog — keep L2 on the page.
    const target = resolveDrilldownTarget(cfg);
    if (!target?._id) return;

    const params = new URLSearchParams();
    params.set('f', cfg.passField);
    params.set('v', String(value));
    if (activeDrilldownReport?._id) {
      params.set('from', activeDrilldownReport._id);
    }
    navigate(
      `${routePrefix}/${activeSuiteOpt.suiteId}/drilldown/${target._id}?${params.toString()}`,
    );
  };

  const hasAnyReportSuite = useMemo(
    () => configs.some((c) => (c.reportSuites?.length ?? 0) > 0),
    [configs],
  );

  return (
    <Box sx={{ pl: { xs: 1.25, md: 2.5 }, pt: 1, width: '100%', mb: 3 }}>
      {/* Toolbar */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: { xs: 1, md: 1.5 },
          ...reportsToolbarSurfaceSx,
        }}
      >
        {configsLoading && (
          <LinearProgress sx={reportsToolbarLoadingLinearProgressSx(theme)} />
        )}
        {/*
          Desktop / tablet (≥ md): server + suite + Logs-style time range inline.
          Mobile: server/suite/time live in the dialog opened from the burger button.
        */}
        <FormControl
          size="small"
          sx={{ minWidth: 200, flex: { xs: 1, md: '0 1 220px' }, display: { xs: 'none', md: 'inline-flex' } }}
        >
          <InputLabel>ClickHouse Server</InputLabel>
          <Select
            label="ClickHouse Server"
            value={activeConfigId}
            onChange={(e) => handleConfigChange(e.target.value as string)}
            disabled={configsLoading || configs.length === 0}
          >
            {configs.length === 0 && (
              <MenuItem value="" disabled>No ClickHouse servers configured</MenuItem>
            )}
            {configs.map((cfg) => {
              const logo = MODULE_LOGO_MAP[cfg.moduleType];
              return (
                <MenuItem key={cfg._id} value={cfg._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {logo && (
                      <img src={`${LOGO_BASE}/${logo}`} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                    )}
                    <Typography variant="body2">{cfg.name}</Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <ReportSuiteFilter
          suites={suitesForConfig}
          valueId={activeSuiteIdOnly}
          onChange={handleSuiteChange}
          disabled={!activeConfigId || suitesForConfig.length === 0}
          hiddenOnMobile
        />

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
          }}
        >
          <Tooltip title="Zoom in time range">
            <span>
              <IconButton
                size="small"
                onClick={timeRange.handleZoomIn}
                disabled={timeRange.timeRangeLabel === REPORTS_QUICK_RANGES[0].label}
                sx={reportsTimeRangeIconButtonSx(theme)}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Shift time range back">
            <IconButton
              size="small"
              onClick={() => timeRange.shiftTime('back')}
              sx={reportsTimeRangeIconButtonSx(theme)}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color={theme.palette.mode === 'light' ? undefined : 'secondary'}
            onClick={timeRange.handleTimePickerOpen}
            startIcon={<AccessTime sx={{ fontSize: 16 }} />}
            sx={reportsTimeRangePickerButtonSx(theme)}
          >
            {timeRange.timeRangeLabel}
          </Button>
          <Tooltip title="Shift time range forward">
            <IconButton
              size="small"
              onClick={() => timeRange.shiftTime('forward')}
              sx={reportsTimeRangeIconButtonSx(theme)}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out time range">
            <IconButton
              size="small"
              onClick={timeRange.handleZoomOut}
              sx={reportsTimeRangeIconButtonSx(theme)}
            >
              <ZoomOut fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Tooltip title="Refresh all panels">
          <IconButton
            onClick={() => void fetchSuiteDetail()}
            sx={reportsToolbarIconButtonSx(theme)}
            aria-label="Refresh all panels"
          >
            <Refresh />
          </IconButton>
        </Tooltip>

        <Tooltip title="Share report suites">
          <span>
            <IconButton
              onClick={() => setShareDialogOpen(true)}
              sx={reportsToolbarIconButtonSx(theme)}
              aria-label="Share"
              disabled={!activeConfigId || suitesForConfig.length === 0}
            >
              <Share />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Saved reports">
          <span>
            <IconButton
              onClick={() => navigate(`${routePrefix}/${activeSuiteIdOnly}/saved-reports`)}
              sx={reportsToolbarIconButtonSx(theme)}
              aria-label="Saved reports"
              disabled={!activeSuiteIdOnly}
            >
              <BookmarksOutlined />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Server, suite & time range">
          <IconButton
            onClick={() => setMobileSuiteDialogOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, ...reportsToolbarIconButtonSx(theme) }}
            aria-label="Server, suite and time range"
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {/*
          Toolbar tiles (counter visualizations / mode='toolbar'). Sit
          inline so they read as part of the toolbar — i.e. they update
          when the user changes the time range above. Hidden on mobile;
          on phones we show them as a compact strip below the toolbar to
          keep the toolbar row uncluttered.
        */}
        {activeSuiteOpt && toolbarReports.length > 0 && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1, alignItems: 'stretch', flexWrap: 'wrap',
              ml: 1,
            }}
          >
            {toolbarReports.map((r) => (
              <CounterTile
                key={r._id}
                report={r}
                tenantId={activeSuiteOpt.tenantId}
                siteId={activeSuiteOpt.siteId}
                timeRangeOverride={timeRangeOverride}
                timezone={suiteDetail?.timezone}
              />
            ))}
          </Box>
        )}

      </Box>

      {serverHasNoSuites && selectedConfig && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {`There are no Report Suites configured against the selected server ${selectedConfig.name}`}
        </Alert>
      )}

      {/* Mobile-only counter strip — same data, different position. */}
      {activeSuiteOpt && isMobile && toolbarReports.length > 0 && (
        <Box sx={{
          display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'center',
        }}>
          {toolbarReports.map((r) => (
            <CounterTile
              key={r._id}
              report={r}
              tenantId={activeSuiteOpt.tenantId}
              siteId={activeSuiteOpt.siteId}
              timeRangeOverride={timeRangeOverride}
              timezone={suiteDetail?.timezone}
            />
          ))}
        </Box>
      )}

      {/*
        Mobile-only context strip. The brief is to keep this strip clean —
        only the active Report Suite is exposed (tappable, opens the
        picker dialog), centered horizontally so it reads as a header
        rather than a leading nav element. Tenant / site are intentionally
        hidden on mobile to avoid visual clutter; they remain visible on
        the desktop toolbar for power users.
      */}
      {isMobile && activeConfigId && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Chip
            size="small"
            color="secondary"
            label={
              activeSuiteOpt
                ? `${activeSuiteOpt.configName} · ${activeSuiteOpt.suiteName}`
                : `${selectedConfig?.name ?? 'Server'} · no suite`
            }
            onClick={() => setMobileSuiteDialogOpen(true)}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}

      <TimePickerPopover
        anchorEl={timeRange.anchorEl}
        onClose={() => timeRange.setAnchorEl(null)}
        timeRangeLabel={timeRange.timeRangeLabel}
        onQuickRangeSelect={(r) => {
          const full = REPORTS_QUICK_RANGES.find((q) => q.label === r.label);
          if (full) timeRange.handleQuickRangeSelect(full);
        }}
        onApplyCustomRange={timeRange.handleApplyCustomRange}
        initialFrom={pickerDates.from}
        initialTo={pickerDates.to}
        tzName={timeRange.tzName}
        tzString={timeRange.tzString}
        maxCustomRangeMs={REPORTS_CUSTOM_TIME_MAX_MS}
        quickRanges={REPORTS_QUICK_RANGES}
      />

      <Dialog
        open={mobileSuiteDialogOpen}
        onClose={() => setMobileSuiteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography variant="h6" component="span" fontWeight={600}>Server, suite &amp; time</Typography>
          <Tooltip title="Close">
            <Button
              color="secondary"
              variant="contained"
              size="small"
              onClick={() => setMobileSuiteDialogOpen(false)}
              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>ClickHouse Server</InputLabel>
            <Select
              label="ClickHouse Server"
              value={activeConfigId}
              onChange={(e) => handleConfigChange(e.target.value as string)}
              disabled={configsLoading || configs.length === 0}
            >
              {configs.length === 0 && (
                <MenuItem value="" disabled>No ClickHouse servers configured</MenuItem>
              )}
              {configs.map((cfg) => {
                const logo = MODULE_LOGO_MAP[cfg.moduleType];
                return (
                  <MenuItem key={cfg._id} value={cfg._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {logo && (
                        <img src={`${LOGO_BASE}/${logo}`} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                      )}
                      <Typography variant="body2">{cfg.name}</Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <ReportSuiteFilter
            suites={suitesForConfig}
            valueId={activeSuiteIdOnly}
            onChange={handleSuiteChange}
            disabled={!activeConfigId || suitesForConfig.length === 0}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Time range
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Tooltip title="Zoom in time range">
                <span>
                  <IconButton
                    size="small"
                    onClick={timeRange.handleZoomIn}
                    disabled={timeRange.timeRangeLabel === REPORTS_QUICK_RANGES[0].label}
                    sx={reportsTimeRangeIconButtonSx(theme)}
                  >
                    <ZoomIn fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Shift time range back">
                <IconButton
                  size="small"
                  onClick={() => timeRange.shiftTime('back')}
                  sx={reportsTimeRangeIconButtonSx(theme)}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                color={theme.palette.mode === 'light' ? undefined : 'secondary'}
                onClick={timeRange.handleTimePickerOpen}
                startIcon={<AccessTime sx={{ fontSize: 16 }} />}
                sx={reportsTimeRangePickerButtonSx(theme)}
              >
                {timeRange.timeRangeLabel}
              </Button>
              <Tooltip title="Shift time range forward">
                <IconButton
                  size="small"
                  onClick={() => timeRange.shiftTime('forward')}
                  sx={reportsTimeRangeIconButtonSx(theme)}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom out time range">
                <IconButton
                  size="small"
                  onClick={timeRange.handleZoomOut}
                  sx={reportsTimeRangeIconButtonSx(theme)}
                >
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileSuiteDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ShareReportsDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        serverName={selectedConfig?.name || ''}
        suites={suitesForConfig}
        defaultSuiteId={activeSuiteIdOnly || undefined}
      />

      {/* Multi-level row-click drill-down (e.g. Search Terms → term events → event detail). */}
      <Dialog
        open={Boolean(drillStack.length > 0 && activeSuiteOpt)}
        onClose={() => setDrillStack([])}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          {drillStack.length > 1 && (
            <IconButton
              aria-label="Back"
              size="small"
              color="inherit"
              onClick={() => setDrillStack((s) => s.slice(0, -1))}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {drillStack[drillStack.length - 1]?.title || 'Details'}
          </Box>
          <IconButton aria-label="Close" color="inherit" onClick={() => setDrillStack([])} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {drillStack.length > 0 && activeSuiteOpt && (
            <ReportPanel
              key={drillStack.length}
              report={drillStack[drillStack.length - 1].report}
              tenantId={activeSuiteOpt.tenantId}
              siteId={activeSuiteOpt.siteId}
              runtimeFilters={drillStack[drillStack.length - 1].runtimeFilters}
              onRowClick={handleRowClick}
              timezone={suiteDetail?.timezone}
              hidePanelHeader
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Breadcrumb sits directly under the toolbar for a consistent location
          across the dashboard, level-1 and level-2 drill-down views. */}
      <DynamicBreadcrumbs items={breadcrumbItems} />

      {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

      {/* Inline panel drill-down (counter → table or sibling tiles), full width below toolbar.
          Grid is only for panel-click sibling tiles (no row filter). Row-click L2
          sibling tables (Uploaded / Excluded / …) stay stacked full-width. */}
      {panelDrilldown && activeSuiteOpt && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: '55vh', md: 'calc(100vh - 260px)' },
            mb: 2,
          }}
        >
          {linkedL2Reports.length > 1 && drilldownRuntimeFilters.length === 0 ? (
            <Box sx={{
              display: 'grid', gap: 2,
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
            }}>
              {linkedL2Reports.map((report) => (
                <Box
                  key={report._id || report.name}
                  sx={{
                    gridColumn: { md: `span ${Math.min(Math.max(report.layout?.w || 4, 3), 12)}` },
                    minWidth: 0,
                    width: '100%',
                  }}
                >
                  <ReportPanel
                    report={report}
                    tenantId={activeSuiteOpt.tenantId}
                    siteId={activeSuiteOpt.siteId}
                    timeRangeOverride={timeRangeOverride}
                    runtimeFilters={drilldownRuntimeFilters}
                    onRowClick={handleRowClick}
                    onPanelClick={handlePanelClick}
                    timezone={suiteDetail?.timezone}
                    hidePanelHeader={false}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {linkedL2Reports.map((report, idx) => (
                <ReportPanel
                  key={report._id || report.name}
                  report={report}
                  tenantId={activeSuiteOpt.tenantId}
                  siteId={activeSuiteOpt.siteId}
                  timeRangeOverride={timeRangeOverride}
                  runtimeFilters={drilldownRuntimeFilters}
                  contextBadge={idx === 0 ? drilldownBadge : undefined}
                  onRowClick={handleRowClick}
                  onPanelClick={handlePanelClick}
                  timezone={suiteDetail?.timezone}
                  hidePanelHeader={idx === 0 ? !drilldownBadge : false}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Empty states — hidden while a panel drill-down is open. */}
      {!panelDrilldown && !hasAnyReportSuite && !errorMsg && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <DashboardOutlined sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No report suites configured</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a report suite from a ClickHouse configuration to start viewing reports.
          </Typography>
          <Button variant="outlined" color="secondary" href="/#/infralytiqs/settings">Go to Settings</Button>
        </Box>
      )}

      {!panelDrilldown && activeSuiteOpt && suiteDetail
        && dashboardReports.length === 0
        && realtimeReports.length === 0
        && toolbarReports.length === 0
        && !loadingSuite && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>This suite has no reports yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Open the suite in settings to add your first report.
          </Typography>
          <Button
            variant="contained" color="secondary"
            href={`/#/infralytiqs/settings/${activeSuiteOpt.configId}/suites/${activeSuiteOpt.suiteId}`}
          >
            Manage Reports
          </Button>
        </Box>
      )}

      {!panelDrilldown && loadingSuite && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: reportsToolbarAccentColor(theme) }} />
        </Box>
      )}

      {/*
        Dashboard grid.
        - `minmax(0, 1fr)` (instead of plain `1fr`) is the grid-layout fix
          that lets children with intrinsic min-content (wide tables, maps,
          long chips) shrink to fit the column. Without it, a wide table
          stretches the grid track and breaks the "stack on mobile" intent.
        - On the wrapper Box for each panel we set `minWidth: 0` for the
          same reason: it lets `<TableContainer overflow-x: auto>` produce
          the horizontal scrollbar inside the panel rather than letting
          the table push the panel beyond the viewport.
      */}
      {/*
        Realtime panels live ABOVE the standard dashboard grid. They poll
        every N seconds and have a moving x-axis, so we don't want them
        intermixed with snapshot panels — the visual disconnect would be
        confusing. Each realtime panel renders full-width by design.
      */}
      {!panelDrilldown && !loadingSuite && activeSuiteOpt && realtimeReports.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {realtimeReports.map((r) => (
            <RealtimePanel
              key={r._id}
              report={r}
              tenantId={activeSuiteOpt.tenantId}
              siteId={activeSuiteOpt.siteId}
              timezone={suiteDetail?.timezone}
              onEdit={canEditReportPanels ? handleEditReport : undefined}
            />
          ))}
        </Box>
      )}

      {!panelDrilldown && !loadingSuite && activeSuiteOpt && dashboardReports.length > 0 && (
        <Box sx={{
          display: 'grid', gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
        }}>
          {dashboardReports.map((report) => (
            <ReportPanelResizeWrapper
              key={report._id}
              report={report}
              canResize={canResizeReportPanels}
              canReorder={canReorderReportPanels}
              onResizeEnd={handleReportLayoutChange}
              onReorderDragStart={(id) => setReorderDragId(id)}
              onReorderDragEnd={() => setReorderDragId(null)}
              onReorderDrop={handleReportReorder}
              isReorderActive={reorderDragId !== null}
              isReorderSource={reorderDragId === report._id}
            >
              <ReportPanel
                report={report}
                tenantId={activeSuiteOpt.tenantId}
                siteId={activeSuiteOpt.siteId}
                timeRangeOverride={timeRangeOverride}
                onRowClick={handleRowClick}
                onPanelClick={handlePanelClick}
                onEdit={canEditReportPanels ? handleEditReport : undefined}
                timezone={suiteDetail?.timezone}
              />
            </ReportPanelResizeWrapper>
          ))}
        </Box>
      )}
    </Box>
  );
};